import { useMemo, FC } from 'react'
import { useQuery } from '@apollo/client'

import {
  filterPackagesByQtyFilter,
  filterTEvoTicketGroupsByQtyFilter,
  filterTEvoTicketGroupsByKeywordsFilter,
  sortTEvoTicketGroupsByPrice,
} from 'components/contexts/event-page-filters/utils'
import { useStore as useFilterStore } from 'components/contexts/event-page-filters'
import { useStore as useEventPageState } from 'components/contexts/event-page'
import { Flex, Grid, NoData, Spinner } from 'components/ui'
import { ArtworkNode } from 'gql'
import { GRANTED_PACKAGES } from 'gql/platform/queries'
import { TEVO_TICKETS } from 'gql/artwork/queries'
import GrantedTickets from './granted'
import TevoTickets from './tevo'
import TicketsWrapper from './wrapper'

type TicketsProps = Pick<ArtworkNode, 'eventExtendedStatus'> & {
  eventSlug: string
  eventPk: string
  hideTEvoTickets?: boolean
}

const Tickets: FC<TicketsProps> = ({
  eventExtendedStatus,
  eventSlug,
  eventPk,
  hideTEvoTickets,
}) => {
  const { sectionIds } = useEventPageState()

  const { quantity, sort, keywords } = useFilterStore()

  const tevo = useQuery(TEVO_TICKETS, {
    variables: { artworkPk: eventPk, tevoTicketsLimit: 20 },
    skip: hideTEvoTickets,
  })

  const granted = useQuery(GRANTED_PACKAGES, {
    variables: { artworkSlug: eventSlug, isTicket: true },
  })

  const tevoTicketGroups = useMemo(
    () => tevo.data?.findArtwork.tevoTicketGroups || [],
    [tevo.data?.findArtwork.tevoTicketGroups]
  )

  const tevoTickets = useMemo(
    () =>
      sortTEvoTicketGroupsByPrice(
        filterTEvoTicketGroupsByKeywordsFilter(
          filterTEvoTicketGroupsByQtyFilter(
            !sectionIds?.length
              ? tevoTicketGroups
              : tevoTicketGroups.filter(({ tevoSectionName }) =>
                  sectionIds.includes(tevoSectionName)
                ),
            quantity
          ),
          keywords
        ),
        sort
      ),
    [sort, keywords, quantity, sectionIds, tevoTicketGroups]
  )

  const grantedTickets = useMemo(
    () =>
      filterPackagesByQtyFilter(
        granted.data?.allPackages.edges.map(({ node }) => node) || [],
        quantity
      ),
    [granted.data?.allPackages.edges, quantity]
  )

  const loading = useMemo(() => tevo.loading || granted.loading, [granted.loading, tevo.loading])

  if (
    (grantedTickets.length === 0 && tevoTickets.length === 0 && !loading) ||
    eventExtendedStatus === 'started'
  ) {
    return <NoData description="No tickets available" />
  }

  if (loading) {
    return (
      <Flex minHeight="200px" alignItems="center" padding="1rem" justifyContent="center">
        <Spinner />
      </Flex>
    )
  }

  return (
    <Grid padding="0 1.5rem" gap="2rem">
      <TicketsWrapper title={{ color: 'light-gray', text: 'best offers' }}>
        <TevoTickets
          data={tevoTicketGroups.filter(({ discount = undefined }) => discount)}
          artworkPk={eventPk}
          withFlashSale
        />
      </TicketsWrapper>

      <TicketsWrapper title={{ color: 'light-gray', text: 'all tickets' }}>
        <GrantedTickets data={grantedTickets} artworkSlug={eventSlug} />
        {!hideTEvoTickets && <TevoTickets data={tevoTickets} artworkPk={eventPk} />}
      </TicketsWrapper>
    </Grid>
  )
}

export default Tickets
